import React, { useState, useEffect } from "react";
import ApiService from "../../services/Api.service";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../Alert/Alert";
import Pagination from "../Pagination/Pagination";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../FilterBar/FilterBar";
import Loader from "../Loader/Loader";
import TableSubSection from "./TableSubSection";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import BulkRecrodingJobs from "./BulkRecrodingJobs";

export default function AllCalls() {
    const [Loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [sections, setSections] = useState([]);
    const [postsPerPage, setPostsPerPage] = useState(20);
    const [headers, setHeaders] = useState([]);
    const [dropDownData, setDropDownData] = useState([]);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [dispositionOptions, setDispositionOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);

    const [activeCallId, setActiveCallId] = useState(null);
    const [activeTab, setActiveTab] = useState("All Calls");
    const tabs = ["All Calls", "Download Jobs"];
    const [lastFilters, setLastFilters] = useState({});


    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        total_pages: 0,
    });
    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);

    // minimal validation (kept empty as original)
    const validationSchema = yup.object().shape({});

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            campaign_id: "",
            agents: [],
            products: [],
            search: "",
            start_date: "",
            end_date: "",
        },
    });

    // fetch campaign options
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const campaignRes = await ApiService.getCampaignOptions();
            const campaignData = campaignRes?.data?.data || [];
            setCampaignOptions(campaignData);
            const disResponse = await ApiService.getDispositionOptions();
            const disData = disResponse?.data?.data || [];
            setDispositionOptions(disData)
            const statusOptionsRes = await ApiService.getCallStatusOptions();
            const statusData = statusOptionsRes?.data?.data || [];
            setStatusOptions(statusData)
        } catch (err) {
            console.error("Error loading dashboard data:", err);
        } finally {
            setLoading(false);
        }
    };

    // fetch dropdown (subprocesses)
    const fetchDropdownDetail = async () => {
        try {
            const subprocessRes = await ApiService.getSubprocesses();
            if (subprocessRes.status === 200) setDropDownData(subprocessRes.data);
        } catch (error) {
            console.error(error);
        }
    };

    // main detail fetch with proper loading handling
    const fetchDetail = async (page = 1, size = postsPerPage, payload = lastFilters) => {
        try {
            setLoading(true);
            if (!payload) {
                payload = {
                    campaign_id: "",
                };
            }
            setLastFilters(payload);
            const res = await ApiService.getAllCallList(payload, { page, size });
            if (res.status === 200 && res.data) {
                setTableData(res.data.data || []);
                setPagination(res.data.pagination || { page, size, total: 0, total_pages: 0 });
                setHeaders(res.data.header || []);
                setSections(res.data.sections || []);
                setSelectedRowIndex(null);
            } else {
                // If non-200, clear table
                setTableData([]);
                setHeaders([]);
                setSections([]);
            }
        } catch (error) {
            console.error("fetchDetail error:", error);
        } finally {
            setLoading(false);
            setActiveCallId(null);
        }
    };

    // Combined initial effect to avoid duplicate calls
    useEffect(() => {
        fetchDropdownDetail();
        fetchDashboardData();
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handelSubmit = async (data) => {
        // Build payload from filter values (ignore empty strings and empty arrays)
        const payload = Object.fromEntries(
            Object.entries(data).filter(([_, v]) => {
                if (Array.isArray(v)) return v.length > 0;
                return v !== "" && v !== null && v !== undefined;
            })
        );
        fetchDetail(1, postsPerPage, payload);
    };

    const handleClearFilters = () => {
        reset({ campaign_id: "", agents: [], products: [], search: "", date_from: "" });
        fetchDetail();
        setActiveCallId(null);
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPostsPerPage(newSize);
        fetchDetail(1, newSize);
        setSelectedRowIndex(null);
        setActiveCallId(null);
    };

    const mistableTh = {
        backgroundColor:"transparent",
        color: "rgba(43, 58, 103, 1)",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "0.1em",
        textAlign: "center",
    };

    const tableTdStyles = {
        backgroundColor: "#F7F7F7",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: "700",
        lineHeight: "16px",
        letterSpacing: "0em",
        textAlign: "center",
        color: "#232122",
    };

    // FilterBar fields (wired to campaignOptions loaded from API)
    const filterDefs = [
        { type: "text", name: "search", placeholder: "Search..." },
        { type: "select", name: "campaign_id", placeholder: "Select Campaign", options: campaignOptions },
        { type: "multiselect", name: "disposition", placeholder: "Select Disposition - All", options: dispositionOptions },
        { type: "multiselect", name: "status", placeholder: "Select Status - All", options: statusOptions },
        { type: "date", name: "start_date", placeholder: "Start Date" },
        { type: "date", name: "end_date", placeholder: "End Date" },
    ];

    const handleAudioDownload = () => {
        createAudioDownloadJob(lastFilters);
    };

    const createAudioDownloadJob = async (data) => {
        try {
            const subprocessRes = await ApiService.createRecordingJob(data);
            if (subprocessRes && (subprocessRes.status === 200 || subprocessRes.status === 201)) {
                setAlertValue({
                    show: true,
                    message: "Audio download job created successfully.",
                    variant: "success",
                    position: "middle",
                });
            } else {
                setAlertValue({
                    
                    show: true,
                    message: "Failed to create audio download job.",
                    variant: "danger",
                    position: "middle",
                });
            }
        } catch (error) {
            console.error("createAudioDownloadJob error:", error);
            setAlertValue({
                show: true,
                message: error.response?.data?.detail || "Failed to create user",
                variant: "danger",
                position: "middle",
            });
        } finally {
            // auto-dismiss alert after a short timeout
            setTimeout(() => {
                setAlertValue({ show: false, message: "", variant: "" });
            }, 2500);
        }
    };

    return (
        <>
            {alertValue.show && (
                <Alert
                    variant={alertValue.variant}
                    message={alertValue.message}
                    dismissible={true}
                    timeout={2500}
                    position={alertValue.position}
                    onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
                />
            )}

            <div className="main-contanier">
                <div className="bulk-download-call-tabs">
                    {tabs.map((tab) => (
                        <div
                            key={tab}
                            className={`tab ${activeTab === tab ? "active" : ""}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </div>
                    ))}
                </div>

                {activeTab === "All Calls" && (
                    <>
                        <FilterBar
                            control={control}
                            handleSubmit={handleSubmit}
                            onSubmit={handelSubmit}
                            onClearFilters={handleClearFilters}
                            filters={filterDefs}
                            title={"All Calls"}
                            totalCount={pagination.total || 0}
                            tableId="table-id"
                            isDownload={false}
                            isDownloadAudio={true}
                            hendleDownloadAudio={handleAudioDownload}
                            isServerExport={true}
                        />

                        <div className="table-container dailymis-table" style={{ height: "calc(100vh - 210px)", position: "relative" }}>
                            {Loading && <Loader fullScreen={false} message="Loading leads..." />}
                            <table id="table-id" className="custom-table-container">
                                <thead className="table-head" style={{ position: "sticky", top: "0", zIndex: "1" }}>
                                    <tr>
                                        <th style={mistableTh}>S.No</th>
                                        {headers.map((header, i) => (
                                            <th key={i} style={mistableTh}>
                                                {header.label}
                                            </th>
                                        ))}
                                        <th style={mistableTh}> Recording</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {tableData && tableData.length > 0 ? (
                                        tableData.map((item, index) => (
                                            <React.Fragment key={index}>
                                                <tr
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setSelectedRowIndex(selectedRowIndex === index ? null : index)}
                                                >
                                                    <td
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </td>
                                                    {headers.map((header, i) => (
                                                        <td
                                                            key={i}
                                                            style={{
                                                                ...tableTdStyles,
                                                                backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                            }}
                                                            title={item[header.value] ?? "--"}
                                                        >
                                                            {header.value === "status" ? (
                                                                <StatusBadge status={item.status} />
                                                            ) : header.value === "disposition" ? (
                                                                <StatusBadge status={item.disposition} />
                                                            ) : (
                                                                <span className="cell-ellipsis">{item[header.value] ?? "--"}</span>
                                                            )}
                                                        </td>
                                                    ))}
                                                    <td
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor: selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                    >
                                                        <AudioPlayer
                                                            callId={item.id}
                                                            duration={item.duration}
                                                            activeCallId={activeCallId}
                                                            setActiveCallId={setActiveCallId}
                                                        />
                                                    </td>
                                                </tr>

                                                {/* Expanded Sub Section: commented out in original; keep option */}
                                                {/* {selectedRowIndex === index && (
                                                    <tr>
                                                        <td colSpan={headers.length + 1}>
                                                            <TableSubSection data={item} sections={sections} />
                                                        </td>
                                                    </tr>
                                                )} */}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={headers.length + 2} style={tableTdStyles}>
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            pagination={pagination}
                            postsPerPage={postsPerPage}
                            handlePageSizeChange={handlePageSizeChange}
                            fetchDetail={fetchDetail}
                        />
                    </>
                )}

                {activeTab === "Download Jobs" && <BulkRecrodingJobs />}
            </div>
        </>
    );
}
